<template>
  <div>
    <div id="header-popup-offer" class="make-offer-header">
      <h1>{{ isNewProduct ? 'Add Product' : 'Edit Product' }}</h1>
      <div class="make-offer-action">
        <button
          class="btn btn-primary"
          :disabled="isLoading || confirmDelete"
          @click="closeModal"
        >
          Cancel
        </button>

        <button
          v-if="!isNewProduct"
          class="btn btn-primary"
          :disabled="isLoading || confirmDelete"
          @click="showConfirmationDelete"
        >
          Delete
        </button>

        <button
          class="btn btn-primary"
          :disabled="isLoading || confirmDelete"
          @click="submitDraft"
        >
          Save as Draft
        </button>

        <button
          class="btn btn-secondary"
          :disabled="isLoading || confirmDelete"
          @click="submitPublish"
        >
          Publish
        </button>
      </div>
    </div>

    <div ref="errorWrapper" class="error-wrapper">
      <Alert v-if="text" :variant="variant">
        {{ text }}
      </Alert>
    </div>

    <div class="confirmation" v-if="confirmDelete">
      <h4>Are you sure you want to delete this product?</h4>
      <div class="confirmation-actions">
        <a href="#" @click.prevent="deleteProduct(true)">Yes</a>
        <a href="#" @click.prevent="deleteProduct(false)">No</a>
      </div>
    </div>

    <section class="make-offer-content">
      <ValidationObserver slim>
        <form @submit.prevent>
          <div class="form-col-1 mb-2">
            <label class="required">Marketplace</label>
            <multiselect
              v-model="selectedMarketplace"
              :options="marketplaces"
              track-by="id"
              label="name"
              placeholder="Select marketplace"
              :allow-empty="false"
              :searchable="true"
              :disabled="!isNewProduct"
            />
          </div>

          <div class="form-col-1 mb-2">
            <label class="required">Product name</label>
            <input
              type="text"
              class="form-control project-name"
              placeholder="The product name will be automatically generated"
              v-model.trim="form.name"
            >
          </div>

          <div class="form-col-2">
            <div class="form-line">
              <label class="required">Price, euro</label>
              <input
                class="form-control"
                type="text"
                placeholder="0.00"
                inputmode="decimal"
                pattern="[0-9]*[.,]?[0-9]{0,2}"
                autocomplete="off"
                :value="priceValue"
                @input="onPriceInput"
              >
            </div>

            <div class="form-line">
              <label class="required">Deliver date</label>
              <DatePicker
                v-model="form.deadline_at"
                format="YYYY-MM-DD"
                value-type="format"
                placeholder="YYYY-MM-DD"
                :disabled-date="disablePastDates"
                class="w-100"
              />
            </div>
          </div>

          <div class="form-col form-col-2 mb-2">
            <div class="form-col-1">
              <label class="required">Company Name</label>
              <input
                type="text"
                class="form-control"
                v-model.trim="form.company_name"
              >
            </div>

            <div class="form-col-1">
              <label class="required">Project Country</label>
              <multiselect
                v-model="form.company_country"
                :options="resolvedCountries"
                track-by="iso"
                label="name"
                placeholder="Select Country"
                :allow-empty="false"
                :searchable="true"
              />
            </div>
          </div>

          <div class="form-col form-col-1 mb-2">
            <label class="required">Product address</label>
            <input
              type="text"
              class="form-control"
              v-model.trim="form.company_address"
              @input="debouncedGetCoordinates"
            >
          </div>

          <div class="form-col-1 location mb-2">
            <label>Location</label>
            <div class="map-wrapper">
              <Map :modelValue="location"/>
            </div>
          </div>

          <div
            v-for="(pair, idx) in gridFieldsPairs"
            :key="'pair-' + idx"
            class="form-col form-col-2 mb-2"
          >
            <div class="form-col-1" v-for="field in pair" :key="field.id">
              <label :class="{ required: Boolean(field.required) }">
                {{ field.name }}
              </label>

              <multiselect
                v-if="isDropboxField(field)"
                v-model="form.data[field.id]"
                :options="field.values || []"
                track-by="id"
                label="value"
                :multiple="Boolean(field.multiple)"
                :close-on-select="!Boolean(field.multiple)"
                :clear-on-select="!Boolean(field.multiple)"
                :preserve-search="true"
                placeholder="Select option"
                :allow-empty="!Boolean(field.required)"
                :searchable="true"
              />

              <input
                v-else
                type="text"
                class="form-control"
                v-model.trim="form.data[field.id]"
              >
            </div>
          </div>

          <div
            v-for="field in textareaFields"
            :key="'ta-' + field.id"
            class="form-col-1 mb-2"
          >
            <label :class="{ required: Boolean(field.required) }">{{ field.name }}</label>
            <textarea
              class="form-control"
              cols="30"
              rows="5"
              maxlength="500"
              v-model.trim="form.data[field.id]"
            ></textarea>
            <div class="counter__box">
              left character
              <span class="counter">{{ 500 - (String(form.data[field.id] || '').length) }}</span>
            </div>
          </div>

          <div class="form-col-1 mb-2">
            <label class="required">Product details</label>
            <textarea
              class="form-control"
              cols="30"
              rows="5"
              maxlength="500"
              v-model.trim="form.detail"
            ></textarea>
            <div class="counter__box">
              left character
              <span class="counter">{{ detailLeft }}</span>
            </div>
          </div>

          <div class="form-col-1 mb-2">
            <label class="required">Product advantages/features</label>
            <textarea
              class="form-control"
              cols="30"
              rows="5"
              maxlength="500"
              v-model.trim="form.feature"
            ></textarea>
            <div class="counter__box">
              left character
              <span class="counter">{{ featureLeft }}</span>
            </div>
          </div>
        </form>
      </ValidationObserver>
    </section>

    <div class="make-offer-bottom">
      <div class="make-offer-action">
        <button
          class="btn btn-primary"
          :disabled="isLoading || confirmDelete"
          @click="closeModal"
        >
          Cancel
        </button>

        <button
          v-if="!isNewProduct"
          class="btn btn-primary"
          :disabled="isLoading || confirmDelete"
          @click="showConfirmationDelete"
        >
          Delete
        </button>

        <button
          class="btn btn-primary"
          :disabled="isLoading || confirmDelete"
          @click="submitDraft"
        >
          Save as Draft
        </button>

        <button
          class="btn btn-secondary"
          :disabled="isLoading || confirmDelete"
          @click="submitPublish"
        >
          Publish
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import Multiselect from 'vue-multiselect';
import { ValidationObserver } from 'vee-validate';

import MatchmakingService from '@/services/MatchmakingService';
import exception from '@/mixins/exception';
import alert from '@/mixins/alert';

import Alert from '@/components/Alert.vue';
import Map from '@/components/Map.vue';

export default {
  name: 'ProductFormRequest',
  components: {
    DatePicker,
    Multiselect,
    ValidationObserver,
    Alert,
    Map,
  },
  mixins: [exception, alert],
  props: {
    marketplaceId: { type: [Number, String], default: null },
    marketplaceName: { type: String, default: '' },
    marketplaces: { type: Array, default: () => [] },
    countries: { type: Array, default: () => [] },
    fields: { type: [Array, Object], default: () => [] },
    formId: { type: String, required: true },
    isNewProduct: { type: Boolean, default: true },
    product: { type: Object, default: null },
  },
  data() {
    return {
      isLoading: false,
      confirmDelete: false,
      date_create: new Date().getTime(),
      wasEditName: false,
      selectedMarketplaceId: this.marketplaceId || null,
      loadedFields: [],
      token: process.env.VUE_APP_MAPBOX_TOKEN,
      location: {
        lng: 14.3001435907641,
        lat: 47.6077978292689,
        bearing: 0,
        pitch: 0,
        zoom: 8,
      },

      form: {
        id: '',
        name: '',
        price: '',
        deadline_at: '',
        company_name: '',
        company_country: null,
        company_address: '',
        detail: '',
        feature: '',
        offers_allow: 1,
        status: 1,
        data: {},
      },
    };
  },
  computed: {
    selectedMarketplace: {
      get() {
        if (!this.selectedMarketplaceId) return null;
        return (this.marketplaces || [])
          .find((m) => String(m.id) === String(this.selectedMarketplaceId)) || null;
      },
      set(marketplace) {
        this.selectedMarketplaceId = marketplace ? marketplace.id : null;
      },
    },

    resolvedFields() {
      // Edit mode or external `fields` prop wins (used for backwards-compat)
      if (this.fields
        && (Array.isArray(this.fields) ? this.fields.length : Object.keys(this.fields).length)) {
        return this.fields;
      }
      // Otherwise use fields loaded from API for the selected marketplace
      return this.loadedFields;
    },

    resolvedCountries() {
      if (Array.isArray(this.countries) && this.countries.length) return this.countries;
      const m = this.selectedMarketplace;
      if (m && m.filters && Array.isArray(m.filters.countries)) return m.filters.countries;
      return [];
    },

    normalizedFields() {
      const raw = this.resolvedFields;
      let list = [];

      if (Array.isArray(raw)) {
        list = raw.slice();
      } else if (raw && typeof raw === 'object') {
        list = Object.keys(raw).map((id) => ({ id, ...raw[id] || {} }));
      }

      return list
        .map((f) => {
          const field = { ...f };
          field.id = String(field.id);

          if (field.type === 'dropbox') {
            const vals = field.values;

            // values can be: ["Solar","Wind",...] OR object map
            if (Array.isArray(vals)) {
              if (vals.length && typeof vals[0] !== 'object') {
                field.values = vals.map((v, idx) => ({ id: idx, value: v }));
              } else {
                field.values = vals.map((v, idx) => ({
                  id: v && v.id !== undefined ? v.id : idx,
                  value: v && (v.value || v.name) ? (v.value || v.name) : String(v),
                }));
              }
            } else if (vals && typeof vals === 'object') {
              field.values = Object.keys(vals).map((k) => ({ id: Number(k), value: vals[k] }));
            } else {
              field.values = [];
            }
          }

          return field;
        })
        .filter((f) => f && f.id);
    },

    detailLeft() {
      return 500 - String(this.form.detail || '').length;
    },
    featureLeft() {
      return 500 - String(this.form.feature || '').length;
    },

    priceValue: {
      get() {
        const v = this.form.price;
        return v === null || v === undefined ? '' : String(v);
      },
      set(v) {
        this.form.price = this.normalizePrice(v);
      },
    },

    gridFields() {
      return (this.normalizedFields || []).filter((f) => f && f.type !== 'textarea');
    },

    gridFieldsPairs() {
      const pairs = [];
      const list = this.gridFields;

      for (let i = 0; i < list.length; i += 2) {
        pairs.push(list.slice(i, i + 2));
      }

      return pairs;
    },

    textareaFields() {
      return (this.normalizedFields || []).filter((f) => f && f.type === 'textarea');
    },

    defaultProductTitle() {
      const manual = String(this.form.name || '').trim();
      if (this.wasEditName && manual) return manual;

      let firstFieldValue = '';
      let secondFieldValue = '';

      const data = this.form.data || {};
      const keys = Object.keys(data);

      const takeValue = (val) => {
        if (val === null || val === undefined) return '';
        if (Array.isArray(val)) return '';
        if (typeof val === 'object') {
          if (val.value) return String(val.value);
          if (val.name) return String(val.name);
          if (val.label) return String(val.label);
          if (val.id !== undefined && val.id !== null) return String(val.id);
          return '';
        }
        return String(val);
      };

      keys.some((key) => {
        const fieldValue = data[key];

        if (Array.isArray(fieldValue)) {
          fieldValue.forEach((item) => {
            const t = takeValue(item);
            if (!t) return;

            if (!firstFieldValue) firstFieldValue = t;
            else if (!secondFieldValue) secondFieldValue = t;
          });
        } else {
          const t = takeValue(fieldValue);
          if (t) {
            if (!firstFieldValue) firstFieldValue = t;
            else if (!secondFieldValue) secondFieldValue = t;
          }
        }

        return Boolean(firstFieldValue && secondFieldValue);
      });

      let result = '';

      if (this.form.company_country) {
        if (typeof this.form.company_country === 'object') {
          if (this.form.company_country.name) result += `${this.form.company_country.name}-`;
          else if (this.form.company_country.iso) result += `${this.form.company_country.iso}-`;
        } else if (typeof this.form.company_country === 'string') {
          result += `${this.form.company_country}-`;
        }
      }

      if (firstFieldValue) result += `${firstFieldValue}-`;
      if (secondFieldValue) result += `${secondFieldValue}-`;

      result += moment(this.date_create).format('DD-MM-YYYY');

      return result;
    },

    requiredFields() {
      const req = [];

      req.push({ type: 'fixed', key: 'price', label: 'Price, euro' });
      req.push({ type: 'fixed', key: 'deadline_at', label: 'Deliver date' });
      req.push({ type: 'fixed', key: 'company_name', label: 'Company Name' });
      req.push({ type: 'fixed', key: 'company_country', label: 'Project Country' });
      req.push({ type: 'fixed', key: 'company_address', label: 'Location' });
      req.push({ type: 'fixed', key: 'feature', label: 'Product advantages/features' });

      (this.normalizedFields || []).forEach((f) => {
        if (!f || !f.required) return;
        req.push({ type: 'dynamic', field: f, label: f.name });
      });

      return req;
    },
  },
  watch: {
    'form.name': function (val) {
      this.wasEditName = String(val || '').trim().length > 0;
    },

    fields: {
      handler() {
        this.initForm();
      },
    },

    product: {
      handler() {
        this.initForm();
      },
    },

    formId(val) {
      if (!val) return;
      this.initForm();
    },

    marketplaceId(val) {
      this.selectedMarketplaceId = val || null;
    },

    selectedMarketplaceId(newId, oldId) {
      if (String(newId || '') === String(oldId || '')) return;

      // For new products: load the field template for the chosen marketplace
      // and reset dynamic data because the field structure changed.
      if (this.isNewProduct) {
        const data = {};
        this.form.data = data;
        this.loadMarketplaceProductFields(newId);
      }
    },
  },
  created() {
    if (this.isNewProduct && this.selectedMarketplaceId) {
      this.loadMarketplaceProductFields(this.selectedMarketplaceId);
    }
    this.initForm();
  },
  methods: {
    async debouncedGetCoordinates() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(async () => {
        await this.getCoordinates();
      }, 1000);
    },

    async getCoordinates() {
      if (!this.form.company_address) return;
      try {
        const response = await axios.get(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(this.form.company_address)}.json?access_token=${this.token}`,
        );
        const [lng, lat] = response.data.features[0].center;
        this.location.lng = lng;
        this.location.lat = lat;
      } catch (error) {
        console.error('Wrong address');
      }
    },

    onPriceInput(event) {
      const raw = event && event.target ? event.target.value : '';
      const normalized = this.normalizePrice(raw);
      this.priceValue = normalized;

      if (event && event.target && event.target.value !== normalized) {
        // eslint-disable-next-line no-param-reassign
        event.target.value = normalized;
      }
    },

    normalizePrice(value) {
      let v = String(value || '');

      v = v.replace(/,/g, '.');
      v = v.replace(/[^0-9.]/g, '');

      const parts = v.split('.');
      if (parts.length > 2) {
        v = `${parts[0]}.${parts.slice(1).join('')}`;
      }

      if (v.indexOf('.') === 0) v = `0${v}`;

      const dotIndex = v.indexOf('.');
      if (dotIndex !== -1) {
        const intPart = v.slice(0, dotIndex);
        const decPart = v.slice(dotIndex + 1);
        v = `${intPart}.${decPart.slice(0, 2)}`;
      }

      return v;
    },

    priceToNumber(value) {
      const s = String(value === undefined || value === null ? '' : value).trim();
      if (!s) return null;
      const n = Number(s.replace(/,/g, '.'));
      return Number.isFinite(n) ? n : null;
    },

    async loadMarketplaceProductFields(marketplaceId) {
      this.loadedFields = [];
      if (!marketplaceId) return;

      try {
        const response = await MatchmakingService
          .marketplaceProducts({ marketplace_id: marketplaceId });
        const payload = response && response.data ? response.data : [];
        // eslint-disable-next-line no-nested-ternary
        const list = Array.isArray(payload)
          ? payload
          : (payload && Array.isArray(payload.data) ? payload.data : []);

        const template = (list || []).find((p) => p
          && Array.isArray(p.data)
          && p.data.length
          && p.data[0]
          && (p.data[0].id || p.data[0].name));

        this.loadedFields = template && Array.isArray(template.data)
          ? template.data.map((f) => ({ ...f }))
          : [];
      } catch (e) {
        this.loadedFields = [];
      }
    },

    initForm() {
      this.confirmDelete = false;
      this.removeAlert();

      this.date_create = new Date().getTime();
      this.wasEditName = false;

      this.selectedMarketplaceId = this.marketplaceId || null;
      this.form.id = this.formId;
      this.form.name = '';
      this.form.price = '';
      this.form.deadline_at = '';
      this.form.company_name = '';
      this.form.company_country = null;
      this.form.company_address = '';
      this.form.detail = '';
      this.form.feature = '';
      this.form.offers_allow = 1;
      this.form.status = 1;

      const data = {};
      (this.normalizedFields || []).forEach((f) => {
        if (!f || !f.id) return;
        if (f.type === 'dropbox') data[f.id] = f.multiple ? [] : null;
        else data[f.id] = '';
      });
      this.form.data = data;

      if (this.product && !this.isNewProduct) {
        this.setFormFromProduct();
      }
    },

    getProductDataValue(data, fieldId) {
      if (!data || !fieldId) return undefined;
      const id = String(fieldId);

      // object form: { field_2: "333" }
      if (typeof data === 'object' && !Array.isArray(data)) {
        if (Object.prototype.hasOwnProperty.call(data, id)) return data[id];
        return undefined;
      }

      // array form: [{id:"field_2", initValue:"333"}, ...]
      if (Array.isArray(data)) {
        const found = data.find((it) => it && String(it.id) === id);
        if (!found) return undefined;
        return found.initValue ?? found.value ?? found.selected ?? found.idValue;
      }

      return undefined;
    },

    mapDropboxValue(field, raw) {
      const list = Array.isArray(field.values) ? field.values : [];
      const byId = (id) => (
        list.find((o) => String(o.id) === String(id))
        || (id !== null && id !== undefined ? { id, value: String(id) } : null)
      );

      if (field.multiple) {
        // eslint-disable-next-line no-nested-ternary
        const arr = Array.isArray(raw) ? raw : (raw === null || raw === undefined || raw === '' ? [] : [raw]);
        return arr.map(byId).filter(Boolean);
      }

      if (raw === null || raw === undefined || raw === '') return null;
      return byId(raw);
    },

    setFormFromProduct() {
      const p = this.product;
      if (!p) return;

      this.wasEditName = true;

      this.form.name = p.name || '';
      this.form.company_name = p.company_name || '';
      this.form.company_address = p.company_address || '';
      this.form.detail = p.detail || '';
      this.form.feature = p.feature || '';

      if (p.price !== undefined && p.price !== null && String(p.price).trim() !== '') {
        this.form.price = this.normalizePrice(p.price);
      }

      if (p.deadline_at) {
        const raw = String(p.deadline_at).trim();
        const parsed = moment(raw, ['DD.MM.YYYY', 'DD/MM/YY', 'YYYY-MM-DD', 'DD.MM.YY', 'DD/MM/YYYY'], true) || moment(raw);
        if (parsed && parsed.isValid()) this.form.deadline_at = parsed.format('YYYY-MM-DD');
      }

      const iso = (p.company_country && p.company_country.iso)
        || (typeof p.company_country === 'string' ? p.company_country : null);

      if (iso) {
        const found = (this.resolvedCountries || []).find((c) => c && String(c.iso) === String(iso));
        if (found) this.form.company_country = found;
      }

      (this.normalizedFields || []).forEach((f) => {
        if (!f || !f.id) return;

        const rawVal = this.getProductDataValue(p.data, f.id);
        if (rawVal === undefined) return;

        if (f.type === 'dropbox') {
          this.$set(this.form.data, f.id, this.mapDropboxValue(f, rawVal));
        } else {
          this.$set(this.form.data, f.id, rawVal);
        }
      });

      if (this.form.company_address) {
        this.getCoordinates();
      }
    },

    isDropboxField(field) {
      return field && field.type === 'dropbox';
    },

    disablePastDates(date) {
      return date < new Date(new Date().setHours(0, 0, 0, 0));
    },

    closeModal() {
      this.confirmDelete = false;
      this.$emit('closeModal');
    },

    showConfirmationDelete() {
      this.confirmDelete = true;
      const el = document.getElementById('header-popup-offer');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    },

    async deleteProduct(isConfirmed) {
      if (!isConfirmed) {
        this.confirmDelete = false;
        return;
      }
      if (!this.product || !this.product.id) return;

      this.isLoading = true;
      this.removeAlert();

      try {
        await MatchmakingService.deleteMarketplaceProduct(this.product.id);
        this.setAlert({ textArg: 'Product deleted', variantArg: 'success' });
        this.$emit('onDeleteProduct');

        setTimeout(() => {
          this.closeModal();
        }, 300);
      } catch (e) {
        this.exception(e);
        this.setAlert({ textArg: 'Failed to delete product', variantArg: 'danger' });
      } finally {
        this.isLoading = false;
        this.confirmDelete = false;
      }
    },

    submitDraft() {
      this.submit(3);
    },

    submitPublish() {
      this.submit(1);
    },

    isValid() {
      if (!this.selectedMarketplaceId) {
        this.setAlert({ textArg: 'Please select a marketplace', variantArg: 'danger' });
        this.scrollToError();
        return false;
      }

      if (!String(this.defaultProductTitle || '').trim()) {
        this.setAlert({ textArg: 'Please enter product name', variantArg: 'danger' });
        this.scrollToError();
        return false;
      }

      const pv = String(this.priceValue || '').trim();
      if (!pv) {
        this.setAlert({ textArg: 'Please fill "Price, euro"', variantArg: 'danger' });
        this.scrollToError();
        return false;
      }

      for (let i = 0; i < this.requiredFields.length; i += 1) {
        const r = this.requiredFields[i];

        if (r.type === 'fixed') {
          const v = this.form[r.key];
          if (r.key === 'company_country') {
            if (!v) {
              this.setAlert({ textArg: `Please fill "${r.label}"`, variantArg: 'danger' });
              this.scrollToError();
              return false;
            }
          } else if (r.key === 'price') {
            if (!String(this.form.price || '').trim()) {
              this.setAlert({ textArg: `Please fill "${r.label}"`, variantArg: 'danger' });
              this.scrollToError();
              return false;
            }
          } else if (!String(v || '').trim()) {
            this.setAlert({ textArg: `Please fill "${r.label}"`, variantArg: 'danger' });
            this.scrollToError();
            return false;
          }
        }

        if (r.type === 'dynamic') {
          const { field } = r;
          const value = this.form.data[field.id];

          if (field.type === 'dropbox') {
            if (field.multiple) {
              if (!Array.isArray(value) || !value.length) {
                this.setAlert({ textArg: `Please fill "${r.label}"`, variantArg: 'danger' });
                this.scrollToError();
                return false;
              }
            } else if (!value) {
              this.setAlert({ textArg: `Please fill "${r.label}"`, variantArg: 'danger' });
              this.scrollToError();
              return false;
            }
          } else if (!String(value || '').trim()) {
            this.setAlert({ textArg: `Please fill "${r.label}"`, variantArg: 'danger' });
            this.scrollToError();
            return false;
          }
        }
      }

      return true;
    },

    scrollToError() {
      this.$nextTick(() => {
        const el = this.$refs.errorWrapper || document.getElementById('header-popup-offer');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },

    buildPayload(status) {
      const payload = {
        id: this.formId,
        marketplace_id: Number(this.selectedMarketplaceId),
        name: this.defaultProductTitle,
        company_name: this.form.company_name,
        company_country: this.form.company_country ? this.form.company_country.iso : null,
        company_address: this.form.company_address,
        price: this.priceToNumber(this.form.price),
        detail: this.form.detail,
        feature: this.form.feature,
        offers_allow: this.form.offers_allow,
        status,
        data: {},
      };

      if (this.form.deadline_at) {
        payload.deadline_at = moment(this.form.deadline_at, 'YYYY-MM-DD').format('DD.MM.YYYY');
      }

      (this.normalizedFields || []).forEach((field) => {
        if (!field || !field.id) return;

        const dataKey = String(field.id);
        const value = this.form.data[field.id];

        if (field.type === 'dropbox') {
          if (field.multiple) {
            // eslint-disable-next-line no-nested-ternary
            const arr = Array.isArray(value)
              ? value
              : (value === null || value === undefined || value === '' ? [] : [value]);

            payload.data[dataKey] = arr
              .map((v) => (v && typeof v === 'object' ? v.id : v))
              .filter((v) => v !== null && v !== undefined && v !== '');
          } else if (!value) {
            payload.data[dataKey] = null;
          } else {
            payload.data[dataKey] = (value && typeof value === 'object') ? value.id : value;
          }
        } else {
          payload.data[dataKey] = value;
        }
      });

      return payload;
    },

    async submit(status) {
      this.removeAlert();
      if (!this.isValid()) return;

      this.isLoading = true;
      try {
        const payload = this.buildPayload(status);

        if (this.isNewProduct) {
          const res = await MatchmakingService.createMarketplaceProduct(payload);
          this.setAlert({ textArg: 'Product saved', variantArg: 'success' });
          this.$emit('onCreateProduct', res.data);
        } else {
          const res = await MatchmakingService.updateMarketplaceProduct(this.product.id, payload);
          this.setAlert({ textArg: 'Product updated', variantArg: 'success' });
          this.$emit('onUpdateProduct', res.data);
        }

        setTimeout(() => {
          this.closeModal();
        }, 300);
      } catch (e) {
        this.exception(e);
        this.setAlert({ textArg: 'Failed to save product', variantArg: 'danger' });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.error-wrapper {
  margin: 10px 0;
}

.make-offer-content {
  padding: 0 5px;
}

.make-offer-bottom {
  margin-top: 50px;
  display: flex;
  justify-content: flex-end;
}

.required::before {
  content: '*';
  color: red;
  margin-right: 4px;
}
</style>
